import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from 'classnames';
import styles from './Tag.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';

var _excluded = ["children", "className", "color", "leading", "variant", "trailing"];
/**
 * Tags are used to draw a customer’s focus to item traits such as availability, status, or media rating. Items may be products, fulfillment slots, or something else. They are visual only and non-interactive.
 *
 * {@link https://livingdesign.walmart.com/components/tag/ Guidelines}
 * {@link https://livingdesign.walmart.com/components/tag/react/ React documentation}
 *
 * ### Content
 *
 * #### Leading icon
 *
 * The Tag accepts a leading icon through the `leading` prop:
 *
 *  #### Trailing icon
 *
 * The Tag accepts a trailing icon through the `leading` prop:
 *
 * ```tsx
 * import * as React from "react";
 * import * as Icon from "@livingdesign/icons";
 * import { Tag } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   return (
 *     <Tag leading={<Icon.Check />}>
 *       Tag content
 *     </Tag>
 *   );
 * };
 * ```
 *
 * ### Features
 *
 * #### Color
 *
 * Tag comes in six colors:
 *
 * ```tsx
 * <Tag color="blue">Tag content</Tag> (default)
 * <Tag color="gray">Tag content</Tag>
 * <Tag color="green">Tag content</Tag>
 * <Tag color="yellow">Tag content</Tag>
 * <Tag color="orange">Tag content</Tag>
 * <Tag color="pink">Tag content</Tag>
 * <Tag color="purple">Tag content</Tag>
 * <Tag color="red">Tag content</Tag>
 * <Tag color="spark">Tag content</Tag>
 * ```
 *
 * #### Variant
 *
 * Tag has three variants:
 *
 * - `primary`: Use for critical information that requires visual emphasis to draw user attention.
 * - `secondary`: Use for information that requires neutral visibility. (default)
 * - `tertiary`: Use for information that requires low visibility that is not distracting to users.
 *
 * ```tsx
 * <Tag variant="primary">Tag content</Tag>
 * <Tag variant="secondary">Tag content</Tag>
 * <Tag variant="tertiary">Tag content</Tag>
 * ```
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
var Tag = function Tag(props) {
  var _applyCommonProps = applyCommonProps(props, Tag),
    children = _applyCommonProps.children,
    className = _applyCommonProps.className,
    _applyCommonProps$col = _applyCommonProps.color,
    color = _applyCommonProps$col === void 0 ? 'purple' : _applyCommonProps$col,
    leading = _applyCommonProps.leading,
    _applyCommonProps$var = _applyCommonProps.variant,
    variant = _applyCommonProps$var === void 0 ? 'tertiary' : _applyCommonProps$var,
    trailing = _applyCommonProps.trailing,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classNames(styles.tag, color === 'red' && styles.red, color === 'spark' && styles.spark, color === 'green' && styles.green, color === 'yellow' && styles.yellow, color === 'orange' && styles.orange, color === 'pink' && styles.pink, color === 'blue' && styles.blue, color === 'purple' && styles.purple, color === 'gray' && styles.gray, variant === 'primary' && styles.primary, variant === 'secondary' && styles.secondary, variant === 'tertiary' && styles.tertiary, className)
  }, rest), leading && /*#__PURE__*/React.createElement("span", {
    className: styles.leading
  }, leading), children, trailing && /*#__PURE__*/React.createElement("span", {
    className: styles.trailing
  }, trailing));
};
Tag.displayName = 'Tag';

export { Tag };
